import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL, apiCall } from '../events/api/apiClient';
import {
  Box, Grid, Skeleton, Alert,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  EmojiEvents as TrophyIcon,
  SportsSoccer as BallIcon,
  Shield as ShieldIcon,
  EventNote as EventIcon,
} from '@mui/icons-material';

// ─── Constants & utils ───────────────────────────────────────────────────────
import { C, injectStyles } from './constants';
import {
  computeGameStats,
  computePlayerGoalStats,
  winRate,
} from './utils';

// ─── Primitive UI ─────────────────────────────────────────────────────────────
import { BigStat } from './ui';

// ─── Feature cards ────────────────────────────────────────────────────────────
import EntityHeader          from './EntityHeader';
import MatchPerformanceCard  from './MatchPerformanceCard';
import StaffDistributionCard from './StaffDistributionCard';
import EventsCard            from './EventsCard';
import SquadCard             from './SquadCard';
import StaffCard             from './StaffCard';
import PositionsCard         from './PositionsCard';
import PlayerGoalStatsCard   from './PlayerGoalStatsCard';

/* ─── Loading skeleton ──────────────────────────────────────────────────────── */
const DashboardSkeleton = () => (
  <Box sx={{ background: C.bg, minHeight: '100vh', p: 4 }}>
    <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 3, mb: 3, bgcolor: C.card }} />
    <Grid container spacing={3}>
      {[...Array(4)].map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 3, bgcolor: C.card }} />
        </Grid>
      ))}
      {[...Array(3)].map((_, i) => (
        <Grid item xs={12} md={4} key={i + 4}>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 3, bgcolor: C.card }} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

/* ─── Main component ─────────────────────────────────────────────────────────── */
const EntityDashboard = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    injectStyles();
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall((headers) =>
        axios.get(`${BASE_URL}/entities/dashboard/me`, { headers })
      );
      setData(res.data.data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Impossible de charger le tableau de bord.'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Derived data (memoised) ── */
  const derived = React.useMemo(() => {
    if (!data) return null;
    const { entity, teamMembers = [], events = [], games = [] } = data;
    const entityId = entity?._id;

    // Role counts from entity.teamMembers (the schema array)
    const roleCounts = {};
    (entity?.teamMembers || []).forEach(m => {
      roleCounts[m.role] = (roleCounts[m.role] || 0) + 1;
    });

    // Enrich user objects with their entity-member metadata (role, position, jersey)
    const enriched = teamMembers.map(u => {
      const info = (entity?.teamMembers || []).find(
        m => m.userId?.toString() === u._id?.toString()
      );
      return { ...u, teamMemberInfo: info };
    });

    const players   = enriched.filter(u => u.teamMemberInfo?.role === 'player');
    const staff     = enriched.filter(u => u.teamMemberInfo?.role !== 'player');
    const activeM   = (entity?.teamMembers || []).filter(m => m.status === 'active').length;
    const suspended = (entity?.teamMembers || []).filter(m => m.status === 'suspended').length;

    // ── Game stats — uses entity1/entity2 IDs to identify the correct score side ──
    const gameStats    = computeGameStats(games, entityId);
    const liveGames    = games.filter(g => g.status === 'live').length;
    const activeEvents = events.filter(e => ['active', 'open'].includes(e.status)).length;

    // ── Goal stats per player ──
    const goalStats = computePlayerGoalStats(games, entityId);

    // Field position map for the position chips
    const positionMap = {};
    players.forEach(p => {
      const pos = p.teamMemberInfo?.positionOnField;
      if (pos) positionMap[pos] = (positionMap[pos] || 0) + 1;
    });

    return {
      entity, enriched, players, staff, roleCounts,
      activeM, suspended,
      gameStats, liveGames,
      activeEvents, positionMap,
      goalStats,
      events, games,
    };
  }, [data]);

  /* ── States ── */
  if (loading) return <DashboardSkeleton />;

  if (error) return (
    <Box sx={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
      <Alert
        severity="error"
        sx={{ background: '#2D0A14', border: `1px solid ${C.rose}40`, color: C.text, fontFamily: 'DM Sans', borderRadius: 3 }}
      >
        {error}
      </Alert>
    </Box>
  );

  if (!derived) return null;

  const {
    entity, enriched, players, staff, roleCounts, activeM, suspended,
    gameStats, liveGames,
    activeEvents, positionMap,
    goalStats,
    events, games,
  } = derived;

  return (
    <Box sx={{ background: C.bg, minHeight: '100vh', fontFamily: 'DM Sans, sans-serif', pb: 8 }}>

      {/* ── 1. Header ── */}
      <EntityHeader
        entity={entity}
        liveGames={liveGames}
        activeEvents={activeEvents}
      />

      <Box sx={{ px: { xs: 2, md: 5 }, pt: 4 }}>

        {/* ── 2. Top KPI counters ── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[
            { value: players.length,   label: 'Joueurs',        icon: <BallIcon />,   color: C.accent,  delay: 0   },
            { value: activeM,          label: 'Membres actifs', icon: <GroupsIcon />, color: C.emerald, delay: 80  },
            { value: gameStats.total,  label: 'Matchs joués',   icon: <ShieldIcon />, color: C.gold,    delay: 160 },
            { value: events.length,    label: 'Évènements',     icon: <EventIcon />,  color: '#A78BFA', delay: 240 },
          ].map((s, i) => (
            <Grid item xs={6} md={3} key={i}>
              <BigStat {...s} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.5}>

          {/* ── 3. Match performance ── */}
          <Grid item xs={12} md={5}>
            <MatchPerformanceCard gameStats={gameStats} delay={320} />
          </Grid>

          {/* ── 4. Staff breakdown ── */}
          <Grid item xs={12} md={3}>
            <StaffDistributionCard roleCounts={roleCounts} suspended={suspended} delay={400} />
          </Grid>

          {/* ── 5. Events list ── */}
          <Grid item xs={12} md={4}>
            <EventsCard events={events} delay={480} />
          </Grid>

          {/* ── 6. Player goal stats (NEW) ── */}
          <Grid item xs={12} md={6}>
            <PlayerGoalStatsCard players={players} goalStats={goalStats} delay={540} />
          </Grid>

          {/* ── 7. Squad list ── */}
          <Grid item xs={12} md={6}>
            <SquadCard players={players} delay={560} />
          </Grid>

          {/* ── 8. Staff (non-players) ── */}
          <Grid item xs={12} md={6}>
            <StaffCard staff={staff} delay={640} />
          </Grid>

          {/* ── 9. Field positions ── */}
          <Grid item xs={12} md={6}>
            <PositionsCard positionMap={positionMap} delay={720} />
          </Grid>

        </Grid>
      </Box>
    </Box>
  );
};

export default EntityDashboard;
